import * as Sentry from "@sentry/react-native";
import { Platform } from "react-native";
import { API_URL, getDevHost } from "./src/config/constants";

// Native platform check (iOS/Android vs Web)
const isNativePlatform = Platform.OS !== "web";

// Derive Spotlight URL for local development
// Port 8970 is nginx proxy with CORS headers (Spotlight v4.6.0+ restricts CORS to localhost)
const getSpotlightUrl = (): string | undefined => {
  if (!__DEV__) return undefined;
  const url = `http://${getDevHost()}:8970/stream`;
  console.log("[Sentry] Spotlight URL:", url);
  return url;
};

// Create navigation integration for native platforms only
// Native benefits: app startup timing, screen render metrics (TTID), navigation performance
// Web excluded: "idle transaction" model groups all requests unhelpfully
const reactNavigationIntegration = isNativePlatform
  ? Sentry.reactNavigationIntegration({
      enableTimeToInitialDisplay: true,
    })
  : null;

// Export for AppNavigator to register
export { reactNavigationIntegration };

// Initialize Sentry BEFORE any other imports to capture initialization errors
Sentry.init({
  dsn: "https://7a69761dec770b23fc28176969b236db@o4510637017006080.ingest.de.sentry.io/4510637019168848",

  // Environment for filtering in Sentry dashboard
  environment: __DEV__ ? "development" : "production",

  // Performance monitoring (disabled in dev — Spotlight captures errors without traces;
  // 20% in prod to stay within Sentry free tier)
  tracesSampleRate: __DEV__ ? 0 : 0.2,

  // Adds more context data to events (IP address, cookies, user, etc.)
  // For more information, visit: https://docs.sentry.io/platforms/react-native/data-management/data-collected/
  sendDefaultPii: true,

  // Enable Logs
  enableLogs: true,

  // Trace propagation for distributed tracing (mobile→backend)
  // In dev: match common local patterns (API_URL may resolve to different IPs)
  // In prod: use the configured API_URL
  // Disabled for web to avoid "idle transaction" grouping issues
  tracePropagationTargets: isNativePlatform
    ? __DEV__
      ? [
          "localhost",
          "127.0.0.1",
          "10.0.2.2",
          /192\.168\.\d+\.\d+/,
          /172\.\d+\.\d+\.\d+/,
        ]
      : [API_URL]
    : [],

  // Add platform tag for filtering in Sentry dashboard
  initialScope: {
    tags: {
      platform: Platform.OS,
    },
  },

  // Configure Session Replay (disabled proactive capture to conserve quota;
  // on-error replay still active)
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1,
  integrations: [
    Sentry.reactNativeTracingIntegration({
      // Enable automatic HTTP request tracing
      enableHTTPTimings: true,
    }),
    // Automatically capture unexpected HTTP errors as Sentry error events
    // Excludes: 400 (validation), 401 (auth), 409 (business logic), 422 (validation)
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [[402, 408], [410, 421], [423, 499], [500, 599]],
    }),
    Sentry.mobileReplayIntegration(),
    Sentry.feedbackIntegration(),
    // Only include navigation integration on native platforms
    ...(reactNavigationIntegration ? [reactNavigationIntegration] : []),
    // Spotlight for local development - explicitly add integration for trace support
    // SDK's built-in getDefaultSidecarUrl() uses ReactNative DevServer for host detection
    ...(__DEV__
      ? [Sentry.spotlightIntegration({ sidecarUrl: getSpotlightUrl() })]
      : []),
  ],
});

import React from "react";
import { registerRootComponent } from "expo";
import App from "./App";
import { ErrorBoundary } from "./src/components/ErrorBoundary";

// Wrap App with ErrorBoundary for friendly error UI
function Root() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

// Storybook mode — render component catalog instead of app
const STORYBOOK_ENABLED = process.env.EXPO_PUBLIC_STORYBOOK === "true";

let RootComponent: React.ComponentType;

if (STORYBOOK_ENABLED) {
  const StorybookUIRoot = require("./.rnstorybook").default;
  RootComponent = StorybookUIRoot;
} else {
  // Wrap with Sentry for automatic error capture
  RootComponent = Sentry.wrap(Root);
}

// registerRootComponent calls AppRegistry.registerComponent('main', () => App);
// It also ensures that whether you load the app in Expo Go or in a native build,
// the environment is set up appropriately
registerRootComponent(RootComponent);
